/**
 * Browser-side client for the Trustico® Issuance API.
 *
 * Each function corresponds to one /api/issuance/* endpoint on the
 * Trustico® server. The server runs the Automatic Certificate
 * Management Environment (ACME) exchange with Sectigo® on behalf of
 * the customer's browser - see src/server/services/issuanceService.js for
 * the server-side engine and src/server/routes/issuance.js for the HTTP
 * routing layer.
 *
 * All functions throw an ApiError on a non-2xx response. The error
 * message is taken verbatim from the server's error envelope (a
 * sentence, its case untouched since 2026-08-27) so the page can
 * render it without further translation.
 *
 * No crypto operations happen here - the browser's only job is to
 * collect inputs, render server-supplied challenge details, poll for
 * status updates, and download the issued artifacts.
 */

/**
 * Error thrown when an /api/issuance/* call returns a non-2xx
 * response. Carries the HTTP status and the raw response body so
 * callers can branch on either.
 */
export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  /**
   * 2026-06-01 eighth-round code review nit : keep the raw response
   * body OUT of any JSON serialisation of this error. `body` is
   * useful in memory for a caller that wants to branch on the
   * server payload, but a 4xx from GET /configuration carries the
   * External Account Binding (EAB) credentials and the ACME account
   * Private Key. If this error object were ever passed to an error
   * reporter (Sentry, a console.log JSON dump, an analytics
   * breadcrumb), the default Error serialisation plus our own
   * fields would leak that body. toJSON returns only the
   * non-sensitive fields ; the in-memory `body` stays reachable on
   * the live object but never reaches a stringified payload.
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
    };
  }
}

// All API calls share a common base URL. Production mounts the routes
// at /api on the Trustico® Express server ; the Vite dev server
// proxies /api to localhost:8080.
const API_BASE = "/api/issuance";

/**
 * One owner of the network-phase failure contract (2026-08-14
 * go-public review) : an AbortError passes through untouched (it is
 * the timeout contract callers branch on) ; everything else becomes
 * the branded ApiError with `status: 0`, so both network
 * phases (the fetch itself, and the body read that fails separately
 * when the connection drops after the headers arrive) surface one
 * customer message and one status-0 discriminator. ALWAYS throws -
 * an AbortError is re-thrown untouched, everything else becomes the
 * branded ApiError - and the name says so, giving both call sites
 * one unconditional contract (2026-08-14 go-public review : the
 * earlier return-or-throw split made `throw brandedNetworkError(e)`
 * read as unconditional while one branch never reached the throw).
 */
function throwBrandedNetworkError(e) {
  if (e && e.name === "AbortError") throw e;
  throw new ApiError(
    "The request could not reach the Trustico® server. Please check your connection and try again.",
    {
      status: 0,
      body: e && e.message,
    }
  );
}

/**
 * Low-level request helper. Centralises JSON encoding, response
 * parsing, and ApiError construction so every endpoint function below
 * stays a one-liner.
 */
async function call(path, { method = "GET", body, signal } = {}) {
  // credentials : "same-origin" tells the browser to include
  // first-party cookies on this request. An HttpOnly cookie binds
  // each issuance session to its creator (see src/server/routes/issuance.js
  // setSessionsCookie). Stating "same-origin" explicitly removes the
  // dependency on the browser default. The Trustico® server's CORS
  // configuration deliberately does NOT send
  // Access-Control-Allow-Credentials, so cross-origin callers cannot
  // attach cookies to the issuance API ; the issuance Single-Page
  // Application (SPA) calls it same-origin from tools.trustico.com.
  const init = { method, signal, credentials: "same-origin" };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  let resp;
  try {
    resp = await fetch(API_BASE + path, init);
  } catch (e) {
    // Network-level failure (offline, DNS, timeout, fetch aborted, ...).
    throwBrandedNetworkError(e);
  }
  // Parse JSON - every endpoint returns JSON on both success and
  // error paths thanks to the error handler middleware.
  let json = null;
  let text;
  try {
    text = await resp.text();
  } catch (e) {
    // The body read is its own network phase (2026-08-14 go-public
    // review, "error-envelope") : a connection dropping AFTER the
    // response headers arrived rejects HERE, not in the fetch catch
    // above, and previously surfaced the browser's raw lowercase
    // TypeError verbatim inside customer-facing Alerts. Same branded
    // mapping as the fetch phase ; an AbortError passes through
    // untouched because it is the long-call timeout contract the
    // callers branch on.
    throwBrandedNetworkError(e);
  }
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = { raw: text };
    }
  }
  if (!resp.ok) {
    const errMessage =
      (json && json.error) ||
      "The Trustico® server returned an unexpected error.";
    throw new ApiError(errMessage, { status: resp.status, body: json });
  }
  return (json && json.data) || null;
}

/**
 * Create a new issuance session. The server validates inputs, fetches
 * the Sectigo® ACME directory, and returns the new sessionId.
 *
 * input shape :
 *   {
 *     serverId      : "trustico-dv" | "trustico-ov" | "sectigo-dv" | "sectigo-ov",
 *     domains       : string[],
 *     keyTypeId     : "rsa-2048" | "rsa-3072" | "rsa-4096" | "rsa-8192" | "ecc-p256" | "ecc-p384",
 *     eab           : { kid, hmacKey }            // optional when reusing
 *     existing      : { accountKeyPem, accountUrl, certKeyPem?, orderUrl? }  // optional
 *   }
 *
 * Accepts a signal so Step2Configure can bound the call with
 * LONG_CALL_TIMEOUT_MS (operator ruling 2026-10-02) : the form is
 * locked, Start Over included, for as long as this call is in flight,
 * so a connection that never answers must not hold it forever.
 */
export async function apiCreateSession(input, { signal } = {}) {
  return call("/sessions", { method: "POST", body: input, signal });
}

/**
 * Fetch the public-safe view of an existing session. Used on page
 * reload to discover whether a previously-saved sessionId is still
 * valid on the server. Accepts a signal so the restore path can arm
 * its bounded AbortController (SHORT_CALL_TIMEOUT_MS below -
 * 2026-08-14 go-public review, "functionality-wedge" :
 * the pre-restored page renders no controls, so this call hanging
 * wedged the whole tool).
 */
export async function apiGetSession(sessionId, { signal } = {}) {
  return call("/sessions/" + encodeURIComponent(sessionId), { signal });
}

/**
 * Drop the session on the server. Idempotent.
 */
export async function apiDiscardSession(sessionId) {
  return call("/sessions/" + encodeURIComponent(sessionId), {
    method: "DELETE",
  });
}

/**
 * Client-side ceiling for the LONG issuance calls (create, start and
 * finalize), consumed by Step2Configure and Step4Download when arming
 * their AbortControllers. 90 seconds (2026-08-14 go-public review :
 * raised from 60, whose JSDoc claimed the server's branded budget
 * error "always wins the race" - untrue for the large-RSA path).
 *
 * The server's orchestration budget (START_ISSUANCE_BUDGET_MS) starts
 * its clock BEFORE key generation - see budgetStartedAt in
 * startIssuance, src/server/services/issuanceService.js - so an
 * RSA-8192 /start under load spends its keygen queue wait (bounded by
 * LARGE_RSA_KEYGEN_WAIT_DEADLINE_MS) and the key generation itself
 * INSIDE that budget. The budget is only consulted before each signed
 * request, though, and never interrupts work already in flight : a
 * key generation runs to its end before the first check, and the last
 * permitted request runs on past the budget for its own timeout. A
 * legitimate slow success or branded server error can therefore land
 * well after the budget figure ; the SIZING paragraphs of the
 * START_ISSUANCE_BUDGET_MS JSDoc carry that arithmetic, and 90s sits
 * above the worst case they derive. When the server is alive its
 * branded response wins the race, and this ceiling only fires when
 * the connection itself has hung, which previously froze Step 2 /
 * Step 4 with every control disabled and no way out (2026-08-13
 * go-public cleanup, audit : "functionality-wedge" - the signal
 * parameter below existed but no caller passed it).
 */
export const LONG_CALL_TIMEOUT_MS = 90_000;

/**
 * Client-side ceiling for the SHORT issuance calls. 15 seconds.
 *
 * Most of its consumers are one small read of stored session state
 * with no ACME orchestration behind it - the page-restore session GET
 * (Issuance.jsx), the Step 3 challenge-details load (Step3Verify.jsx),
 * ChallengePanel's cooldown resync (resyncCooldownsFromServer) and
 * the two Configuration File fetches (useConfigFileSave.js and
 * Step4Download.jsx) - and for those this is generous headroom.
 *
 * ChallengePanel also arms it around apiTriggerValidation and
 * apiCheckChallenge (Request Validation and Check Validation Status).
 * Those two DO make signed requests to the Certificate Authority (CA)
 * on the server, each outbound request bounded there by
 * ACME_FETCH_TIMEOUT_MS (src/server/services/acme/transport.js), so
 * for them this ceiling is not headroom : it can fire while the server
 * is still working, and an aborted press may already have reached the
 * server. runTrigger and runCheck resync both cooldown windows from
 * the server after an aborted press for exactly that reason.
 *
 * The AbortError lands in the caller's existing branded retry
 * handling ; the cooldown resync alone is best effort and stays
 * silent. WHY a ceiling at all (2026-08-14 go-public review,
 * the "functionality-wedge" family) : a fetch that neither resolves
 * nor rejects (black-holed TCP, wifi drop mid-request) previously
 * wedged whichever surface awaited it - the whole tool on the
 * restore screen, Step 3 on "Loading..." with zero controls, or a
 * Save/Download Configuration File control disabled forever. One
 * shared constant so every short-call surface reads one figure
 * (a second hand-written copy, like the RESTORE_CALL_TIMEOUT_MS
 * this replaced in Issuance.jsx, drifts on the first retune).
 */
export const SHORT_CALL_TIMEOUT_MS = 15_000;

/**
 * The one owner of the arm-abort-clear timeout contract for every
 * bounded issuance call (2026-08-14 go-public review) : construct an
 * AbortController, arm a timer that aborts it after `ms`, run `fn`
 * with the controller's signal, and ALWAYS clear the timer in a
 * finally. Before this helper the same four-line block was
 * hand-written at eight call sites, where a contract change (or a
 * forgotten clearTimeout) would land on some and miss others.
 *
 * `fn` receives (signal, controller). The controller is passed so
 * effect-scoped callers (the page-restore and Step 3 challenge-load
 * effects) can capture it and abort on unmount / StrictMode remount ;
 * plain call sites ignore it. The capture is synchronous : this
 * helper invokes `fn` before its own first await, so an effect body
 * that calls it has the controller in hand before any cleanup can
 * run. The timeout AbortError propagates to the caller's existing
 * branded mapping ; the timer never outlives the call.
 *
 * @param {number} ms - timeout in milliseconds (SHORT_CALL_TIMEOUT_MS
 *   or LONG_CALL_TIMEOUT_MS).
 * @param {(signal: AbortSignal, controller: AbortController) => Promise<any>} fn
 * @returns {Promise<any>} fn's own result, unchanged.
 */
export async function withTimeoutSignal(ms, fn) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fn(controller.signal, controller);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Run the ACME registration + order + authz fetch sequence. Long-
 * running : the caller should show a spinner and bound the call with
 * LONG_CALL_TIMEOUT_MS, whose JSDoc above says how long a legitimate
 * answer can take.
 */
export async function apiStartIssuance(sessionId, { signal } = {}) {
  return call("/sessions/" + encodeURIComponent(sessionId) + "/start", {
    method: "POST",
    signal,
  });
}

/**
 * Fetch the per-domain DNS-01 / HTTP-01 challenge details that the
 * customer needs to deploy. Each entry includes the computed key
 * authorization and the DNS-01 TXT record value already hashed.
 */
export async function apiGetChallenges(sessionId, { signal } = {}) {
  return call("/sessions/" + encodeURIComponent(sessionId) + "/challenges", {
    signal,
  });
}

/**
 * "Request Validation" - asks Sectigo® to validate by POSTing {} to
 * the challenge URL (RFC 8555 §7.5.1). This is the ONLY call that starts
 * validation. The customer presses it ONCE after deploying the DNS / HTTP
 * challenge, and only again if it is still stuck after a while (the
 * server rate-limits rapid re-triggers). Returns the challenge object.
 */
export async function apiTriggerValidation(
  sessionId,
  challengeUrl,
  { signal } = {}
) {
  return call(
    "/sessions/" + encodeURIComponent(sessionId) + "/challenges/trigger",
    { method: "POST", body: { challengeUrl }, signal }
  );
}

/**
 * "Check Validation Status" - the READ-ONLY status poll. Reads the current
 * authorization status without re-triggering anything, so it can never
 * disturb Sectigo®'s in-flight validation. Returns the authorization
 * object (read `.status`). The customer presses this to ask "is it done
 * yet?" whenever they choose, subject to the short CHECK_COOLDOWN_MS
 * floor ; there is no automatic polling.
 */
export async function apiCheckChallenge(
  sessionId,
  challengeUrl,
  { signal } = {}
) {
  return call(
    "/sessions/" + encodeURIComponent(sessionId) + "/challenges/check",
    { method: "POST", body: { challengeUrl }, signal }
  );
}

/**
 * Fetch the resumable Configuration File data (account key, SSL
 * Certificate key, order URL, metadata) for a session. Used to let the
 * customer download a Configuration File mid-flow so they can resume
 * later. Available any time after the session has started.
 */
export async function apiGetConfiguration(sessionId, { signal } = {}) {
  return call("/sessions/" + encodeURIComponent(sessionId) + "/configuration", {
    signal,
  });
}

/**
 * Submit the CSR (once) and check the order status. Resolves to the
 * issued bundle when Sectigo® has signed, or to { stillProcessing:
 * true } (HTTP 202) while it is still signing - in which case the
 * customer presses "Check Progress" to call this again.
 */
export async function apiFinalizeAndIssue(sessionId, { signal } = {}) {
  return call("/sessions/" + encodeURIComponent(sessionId) + "/finalize", {
    method: "POST",
    signal,
  });
}
